<template>
  <div class="publish-queue">
    <el-card shadow="never">
      <template #header>
        <div class="card-header">
          <span>🚀 发布队列（半自动）</span>
          <div>
            <el-button size="small" type="success" plain @click="dialogVisible = true">＋ 手动新增发布</el-button>
            <el-select v-model="filterStatus" size="small" style="width:140px;margin-right:8px" @change="load">
              <el-option label="全部状态" value="" />
              <el-option label="待发布 ready" value="ready" />
              <el-option label="已完成 done" value="done" />
              <el-option label="发布中 publishing" value="publishing" />
              <el-option label="失败 failed" value="failed" />
            </el-select>
            <el-button size="small" @click="load">刷新</el-button>
          </div>
        </div>
      </template>

      <el-alert
        type="info" :closable="false" show-icon
        title="发布流程：半自动（复制文案→跳转App→粘贴发布→确认）｜全自动（模拟器一键发布：剪辑/洗稿/复刻→定时→自动发布）"
        style="margin-bottom:12px"
      />

      <!-- ⚡ 快捷发布入口：指向独立页面 -->
      <el-alert
        type="warning" :closable="false" show-icon
        title="上传视频/图片 → 自动洗稿+剪辑+复刻 → 定时发布，请使用「⚡ 快捷发布」页面（左侧菜单）"
        style="margin-bottom:12px"
      >
        <template #default>
          <el-button size="small" type="warning" plain @click="$router.push('/quick')" style="margin-left:8px">去快捷发布 →</el-button>
        </template>
      </el-alert>

      <el-table :data="rows" v-loading="loading" stripe :row-class-name="rowClass">
        <el-table-column prop="id" label="ID" width="60" />
        <el-table-column label="平台" width="80">
          <template #default="{row}">
            <el-tag size="small">{{ {douyin:'抖音',kuaishou:'快手',xhs:'小红书'}[row.platform] || row.platform }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="标题" min-width="180" show-overflow-tooltip>
          <template #default="{row}">
            <span :class="{ 'no-title': !row.title }">{{ row.title || '（未命名 · 见素材列）' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="类型" width="70">
          <template #default="{row}">
            <el-tag size="small" type="info">{{ {video:'视频',image:'图片',text:'文案'}[row.media_type] || row.media_type }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="加工" width="130">
          <template #default="{row}">
            <el-tag v-if="row.edited_file_path" size="small" type="success">🎬 已剪辑</el-tag>
            <el-tag v-else-if="row.dedupe_level > 0" size="small" type="warning">复刻L{{row.dedupe_level}}</el-tag>
            <el-tag v-else size="small" type="info">原片</el-tag>
            <el-tag v-if="row.rewrite_versions" size="small" type="success" style="margin-left:4px">✍️ 已洗稿</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="模式" width="70">
          <template #default="{row}">
            <el-tag size="small" :type="row.mode === 'auto' ? 'primary' : 'default'">{{ row.mode === 'auto' ? '自动' : '半自动' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="110">
          <template #default="{row}">
            <el-tag size="small" :type="statusType(row.status)">{{ statusText(row.status) }}</el-tag>
            <el-tag v-if="row.scheduled_at && row.status === 'ready'" size="small" type="warning" style="margin-left:4px">⏰ 定时</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="素材" width="140">
          <template #default="{row}">
            <span v-if="row.file_path" class="file-link" :title="row.file_path">{{ row.file_path.split(/[\\/]/).pop() }}</span>
            <el-tag v-else size="small" type="warning">无素材</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" min-width="330">
          <template #default="{row}">
            <div class="row-ops">
              <el-button v-if="row.mode === 'auto'" size="small" type="primary" :disabled="row.status === 'publishing'" @click="doExecute(row)">🚀 自动发布</el-button>
              <el-button v-else size="small" type="primary" plain @click="doExecute(row)">⚡ 生成发布包</el-button>
              <el-button size="small" @click="openAttach(row)">📎 素材</el-button>
              <el-button v-if="row.media_type === 'video' && hasCompare(row)" size="small" type="success" plain @click="openCompare(row)">🎞 对比预览</el-button>
              <el-button v-if="row.auto_log" size="small" @click="showLog(row)">📜 日志</el-button>
              <template v-if="row.mode === 'semi'">
                <el-button size="small" @click="doCopy(row)">📋 复制</el-button>
                <el-button size="small" type="success" plain @click="doJump(row)">📲 跳转</el-button>
                <el-button size="small" type="warning" plain @click="doConfirm(row)">✅ 已发布</el-button>
              </template>
              <el-button size="small" type="danger" plain @click="delRow(row)">🗑 删除</el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>
      <el-empty v-if="!loading && rows.length === 0" description="队列为空 — 去「内容工厂」生成五件套，或点右上角手动新增" />
    </el-card>

    <!-- 手动新增发布 dialog -->
    <el-dialog v-model="dialogVisible" title="＋ 手动新增发布" :width="'min(520px, 94vw)'">
      <el-form label-width="80px" label-position="top">
        <el-form-item label="素材文件（视频/图片，可选）">
          <el-upload :auto-upload="false" :limit="1" :on-change="onFileChange" accept="video/*,image/*">
            <el-button>📁 选择素材</el-button>
          </el-upload>
          <span class="file-name">{{ fileName || '不选则纯文案发布' }}</span>
        </el-form-item>
        <el-row :gutter="12">
          <el-col :span="12">
            <el-form-item label="平台">
              <el-select v-model="form.platform" style="width:100%">
                <el-option label="抖音" value="douyin" />
                <el-option label="快手" value="kuaishou" />
                <el-option label="小红书" value="xhs" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="类型">
              <el-select v-model="form.media_type" style="width:100%">
                <el-option label="文案" value="text" />
                <el-option label="视频" value="video" />
                <el-option label="图片" value="image" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="标题">
          <el-input v-model="form.title" :maxlength="50" show-word-limit />
        </el-form-item>
        <el-form-item label="文案描述">
          <el-input v-model="form.desc" type="textarea" :rows="3" placeholder="粘贴 AI 生成或自己写的文案" />
        </el-form-item>
        <el-form-item label="话题标签">
          <el-input v-model="form.tags" placeholder="逗号分隔，如：UV打印机,设备选购" />
        </el-form-item>
        <el-row :gutter="12">
          <el-col :span="12">
            <el-form-item label="发布模式">
              <el-select v-model="form.mode" style="width:100%">
                <el-option label="全自动（模拟器一键发布）" value="auto" />
                <el-option label="半自动（手动复制发布）" value="semi" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="爆款复刻">
              <el-select v-model="form.dedupe_level" style="width:100%">
                <el-option label="0 - 原片直发" :value="0" />
                <el-option label="1 - 轻度复刻（推荐）" :value="1" />
                <el-option label="2 - 深度复刻" :value="2" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="12">
          <el-col :span="12">
            <el-form-item label="✍️ 文案洗稿（AI改写）">
              <el-switch v-model="form.rewrite" />
              <span class="opt-hint">标题/描述 AI 改写洗稿</span>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="🎬 视频加字剪辑">
              <el-switch v-model="form.edit_video" />
              <span class="opt-hint">标题+副标题加在视频上</span>
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="⏰ 定时发布（可选）">
          <el-date-picker
            v-model="form.scheduled_at"
            type="datetime"
            placeholder="不选=保存后手动发布"
            style="width:100%"
            format="YYYY-MM-DD HH:mm"
            :disabled-date="(t) => t.getTime() < Date.now() - 60000"
          />
          <span class="opt-hint">到点自动触发全自动发布（仅 auto 模式）</span>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="saveManual">保存入队</el-button>
      </template>
    </el-dialog>
    <!-- 选素材 dialog -->
    <el-dialog v-model="attachVisible" :title="'📎 绑定素材 — 发布项 #' + attachId" :width="'min(480px, 94vw)'">
      <el-alert
        type="info" :closable="false" show-icon
        title="上传后素材绑定到此发布项；手机端发布时用「跳转App」打开抖音后，从相册选择该视频/图片即可"
        style="margin-bottom:12px"
      />
      <el-upload drag :auto-upload="false" :limit="1" :on-change="onAttachChange" accept="video/*,image/*" style="width:100%">
        <div style="padding:24px 0">
          <div style="font-size:40px">📁</div>
          <div style="margin-top:8px">拖拽或点击选择视频/图片</div>
          <div class="file-name">{{ attachFileName || '未选择文件' }}</div>
        </div>
      </el-upload>
      <template #footer>
        <el-button @click="attachVisible = false">取消</el-button>
        <el-button type="primary" :loading="attaching" @click="doAttach">绑定素材</el-button>
      </template>
    </el-dialog>
    <!-- 发布日志 dialog -->
    <el-dialog v-model="logVisible" title="📜 发布执行日志" :width="'min(620px, 94vw)'">
      <pre class="log-box">{{ logText }}</pre>
    </el-dialog>
    <!-- 原素材 vs 处理成品 对比预览 dialog -->
    <el-dialog v-model="cmpVisible" title="🎞 对比预览：原素材 vs 处理成品" :width="'min(900px, 96vw)'" top="4vh">
      <el-row :gutter="12">
        <el-col :xs="24" :sm="12" class="cmp-col">
          <div class="cmp-label">📁 原素材（上传/来源原样）</div>
          <video v-if="cmpSrc" class="cmp-video" :src="cmpSrc" controls preload="metadata"></video>
          <div v-else class="cmp-none">无原素材</div>
        </el-col>
        <el-col :xs="24" :sm="12" class="cmp-col">
          <div class="cmp-label">🎬 处理成品（{{ cmpLevelText }}）</div>
          <video v-if="cmpOut" class="cmp-video" :src="cmpOut" controls preload="metadata"></video>
          <div v-else class="cmp-none">无成品</div>
        </el-col>
      </el-row>
      <div class="cmp-tip">💡 左右分别点播放即可对照：画面是否有处理痕迹、加字效果、防搬运去重效果。</div>
    </el-dialog>
  </div>
</template>

<script>
import api from '../api'

export default {
  name: 'PublishQueue',
  data() {
    return {
      rows: [],
      loading: false,
      filterStatus: '',
      dialogVisible: false,
      saving: false,
      fileName: '',
      form: { account_id: 0, platform: 'douyin', media_type: 'text', title: '', desc: '', tags: '', mode: 'auto', dedupe_level: 1, rewrite: true, edit_video: true, scheduled_at: null },
      quickOpen: ['quick'],
      quickFileName: '',
      quickForm: { title: '', dedupe_level: 1, rewrite: true, edit_video: true, scheduled_at: null },
      attachVisible: false,
      attachId: 0,
      attachFileName: '',
      attaching: false,
      cmpVisible: false,
      cmpSrc: '',
      cmpOut: '',
      cmpLevelText: '',
      logVisible: false,
      logText: ''
    }
  },
  created() {
    this.load()
    window.addEventListener('platform-changed', this.load)
    const f = parseInt(this.$route.query.focus || '', 10)
    if (f > 0) this._focusId = f
  },
  beforeUnmount() {
    window.removeEventListener('platform-changed', this.load)
  },
  methods: {
    rowClass({ row }) {
      return row.id === this._focusId ? 'pq-focus-row' : ''
    },
    mediaUrl(p) {
      // 后端 /media/ 静态托管 data/assets → 播放 URL
      if (!p) return ''
      return '/media/' + String(p).split(/[\\/]/).pop()
    },
    hasCompare(row) {
      // 有「处理成品」可对比：剪辑产物 或 复刻后 file_path 已不同于原始 source_path
      if (!row || row.media_type !== 'video') return false
      if (row.edited_file_path) return true
      if (row.source_path && row.file_path && row.file_path !== row.source_path) return true
      return false
    },
    openCompare(row) {
      const src = row.source_path || row.file_path || ''
      const edited = row.file_path !== src && row.file_path ? row.file_path : (row.edited_file_path || '')
      this.cmpSrc = this.mediaUrl(src)
      this.cmpOut = this.mediaUrl(edited)
      this.cmpLevelText = row.dedupe_level > 0 ? '复刻L' + row.dedupe_level + (row.edited_file_path ? ' · 剪辑加字' : '') : '剪辑加字'
      this.cmpVisible = true
    },
    async load() {
      this.loading = true
      try {
        const acc = localStorage.getItem('current_account_id')
        const r = await api.get('/publish/queue', { params: { account_id: acc || 0, status: this.filterStatus } })
        this.rows = r.queue || []
        if (this._focusId) {
          this.$nextTick(() => {
            const el = document.querySelector('.pq-focus-row')
            if (el) {
              el.scrollIntoView({ block: 'center', behavior: 'smooth' })
              this.$message.success('已定位到您刚生成的视频发布项（高亮行）')
            }
          })
        }
      } catch (e) {
        this.$message.error('加载失败：' + e.message)
      } finally {
        this.loading = false
      }
    },
    async delRow(row) {
      // 删除发布项（待发布/失败/已完成记录）；发布中不可删
      try {
        await this.$confirm(`确定删除发布项「${(row.title || '未命名').slice(0, 30)}」吗？将同步清理其待发布素材文件，不可恢复。`, '删除发布项', {
          confirmButtonText: '删除', cancelButtonText: '取消', type: 'warning'
        })
      } catch (e) {
        return
      }
      try {
        const r = await api.delete(`/publish/${row.id}`)
        this.$message.success(r.msg || '已删除')
        this.load()
      } catch (e) {
        this.$message.error(e.message)
      }
    },
    statusText(s) {
      return { pending: '排队', ready: '待发布', publishing: '发布中', done: '已完成', failed: '失败' }[s] || s
    },
    statusType(s) {
      return { ready: 'primary', done: 'success', publishing: 'warning', failed: 'danger' }[s] || 'info'
    },
    async doExecute(row) {
      if (row.mode === 'auto') {
        // 2026-09-03：全自动发布使用 MuMu 模拟器里当前登录的抖音号（不是本队列账号列）
        try {
          await this.$confirm(
            '自动发布将使用「MuMu 模拟器里当前登录的抖音账号」执行，约 90 秒完成。\n\n请确认模拟器已登录您要发布的那个抖音号，否则会发错账号！',
            '发布账号确认', { type: 'warning', confirmButtonText: '确认发布', cancelButtonText: '再想想' }
          )
        } catch (e) {
          return // 用户取消
        }
      }
      try {
        const r = await api.post(`/publish/${row.id}/execute`)
        if (r.mode === 'auto') {
          this.$message.success(r.msg || '全自动发布已启动，约 90 秒完成')
        } else {
          this.$message.success('发布包已生成，可复制文案并跳转 App')
          this._clipboard = r.clipboard
        }
        this.load()
      } catch (e) {
        this.$message.error(e.message)
      }
    },
    showLog(row) {
      this.logText = row.auto_log || '暂无日志'
      this.logVisible = true
    },
    async doCopy(row) {
      let cb = this._clipboard
      if (!cb || cb.title !== row.title) {
        try {
          const r = await api.get(`/publish/${row.id}/clipboard`)
          cb = r.clipboard
          this._clipboard = cb
        } catch (e) {
          this.$message.warning('请先点击「生成发布包」')
          return
        }
      }
      const text = cb.full_text || `${cb.title}\n${cb.desc}`
      try {
        await navigator.clipboard.writeText(text)
        this.$message.success('文案已复制到剪贴板 ✅')
      } catch (e) {
        // 手机浏览器兜底：textarea 选中复制
        const ta = document.createElement('textarea')
        ta.value = text
        document.body.appendChild(ta)
        ta.select()
        document.execCommand('copy')
        document.body.removeChild(ta)
        this.$message.success('文案已复制 ✅')
      }
    },
    doJump(row) {
      const scheme = { douyin: 'snssdk1128://', kuaishou: 'kwai://', xhs: 'xhsdiscover://' }[row.platform] || 'snssdk1128://'
      const isMobile = /Android|iPhone|iPad/i.test(navigator.userAgent)
      if (!isMobile) {
        this.$message.warning('跳转 App 需在手机浏览器打开本页面操作（PC 上请用手机访问）')
        return
      }
      window.location.href = scheme
    },
    async doConfirm(row) {
      try {
        await api.post(`/publish/${row.id}/confirm`)
        this.$message.success('已记录发布 ✅')
        this.load()
      } catch (e) {
        this.$message.error(e.message)
      }
    },
    onFileChange(file) {
      this.fileName = file.name
      this._file = file.raw
    },
    openAttach(row) {
      this.attachId = row.id
      this.attachFileName = ''
      this._attachFile = null
      this.attachVisible = true
    },
    onAttachChange(file) {
      this.attachFileName = file.name
      this._attachFile = file.raw
    },
    async doAttach() {
      if (!this._attachFile) {
        this.$message.warning('请先选择视频/图片文件')
        return
      }
      this.attaching = true
      try {
        const fd = new FormData()
        fd.append('file', this._attachFile)
        await api.post(`/publish/${this.attachId}/attach`, fd, { timeout: 120000 })
        this.$message.success('素材已绑定 ✅')
        this.attachVisible = false
        this.load()
      } catch (e) {
        this.$message.error(e.message)
      } finally {
        this.attaching = false
      }
    },
    async saveManual() {
      if (!this.form.title.trim()) {
        this.$message.warning('请填写标题')
        return
      }
      this.saving = true
      try {
        let filePath = ''
        let mediaType = this.form.media_type
        if (this._file) {
          const fd = new FormData()
          fd.append('file', this._file)
          fd.append('account_id', this.form.account_id)
          const r = await api.post('/assets', fd, { timeout: 120000 })
          filePath = r.file_path
          if (this.form.media_type === 'text') mediaType = r.type
        }
        const sched = this.form.scheduled_at
          ? new Date(this.form.scheduled_at).toISOString()
          : ''
        await api.post('/publish', {
          account_id: this.form.account_id,
          platform: this.form.platform,
          media_type: mediaType,
          file_path: filePath,
          title: this.form.title,
          desc: this.form.desc,
          tags: this.form.tags,
          mode: this.form.mode,
          dedupe_level: this.form.dedupe_level,
          rewrite: this.form.rewrite,
          edit_video: this.form.edit_video,
          scheduled_at: sched
        })
        this.$message.success(sched ? '已入队并设定定时发布 ✅' : '已加入发布队列 ✅')
        this.dialogVisible = false
        this.form = { account_id: 0, platform: 'douyin', media_type: 'text', title: '', desc: '', tags: '', mode: 'auto', dedupe_level: 1, rewrite: true, edit_video: true, scheduled_at: null }
        this.fileName = ''
        this._file = null
        this.load()
      } catch (e) {
        this.$message.error(e.message)
      } finally {
        this.saving = false
      }
    },
    onQuickFile(file) {
      this.quickFileName = file.name
      this._quickFile = file.raw
    },
    async quickSubmit() {
      // ⚡ 快捷发布：上传素材 → 洗稿/剪辑/复刻 → 入队（全自动模式，可定时）
      if (!this._quickFile && !this.quickForm.title.trim()) {
        this.$message.warning('请选择视频/图片，或填写标题')
        return
      }
      this.saving = true
      try {
        let filePath = ''
        let mediaType = 'text'
        if (this._quickFile) {
          const fd = new FormData()
          fd.append('file', this._quickFile)
          fd.append('account_id', localStorage.getItem('current_account_id') || 0)
          const r = await api.post('/assets', fd, { timeout: 120000 })
          filePath = r.file_path
          mediaType = r.type || 'video'
        }
        const sched = this.quickForm.scheduled_at
          ? new Date(this.quickForm.scheduled_at).toISOString()
          : ''
        await api.post('/publish', {
          account_id: localStorage.getItem('current_account_id') || 0,
          platform: 'douyin',
          media_type: mediaType,
          file_path: filePath,
          title: this.quickForm.title,
          desc: '',
          tags: '',
          mode: 'auto',
          dedupe_level: this.quickForm.dedupe_level,
          rewrite: this.quickForm.rewrite,
          edit_video: this.quickForm.edit_video,
          scheduled_at: sched
        })
        this.$message.success(sched ? '已入队并定时，到点自动发布 ✅' : '已入队，自动加工+发布 ✅')
        this.quickForm = { title: '', dedupe_level: 1, rewrite: true, edit_video: true, scheduled_at: null }
        this.quickFileName = ''
        this._quickFile = null
        this.load()
      } catch (e) {
        this.$message.error(e.message)
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.publish-queue { padding: 4px; }
.quick-form { display: flex; flex-wrap: wrap; align-items: center; row-gap: 10px; }
@media (max-width: 767px) {
  .quick-form { flex-direction: column; align-items: flex-start; }
}
.card-header { display: flex; justify-content: space-between; align-items: center; }
.file-link { color: #409eff; font-size: 12px; word-break: break-all; }
.no-title { color: #909399; font-style: italic; }
.row-ops { display: flex; align-items: center; gap: 4px; flex-wrap: wrap; }
.pq-focus-row > td.el-table__cell { background: rgba(250, 204, 21, 0.14) !important; box-shadow: inset 3px 0 0 #facc15; }
.opt-hint { font-size: 11px; color: #909399; margin-left: 6px; }
.log-box { background: #0d1117; color: #7ee787; padding: 12px; border-radius: 6px; max-height: 360px; overflow: auto; font-size: 12px; white-space: pre-wrap; word-break: break-all; }
.cmp-col { margin-bottom: 10px; overflow: hidden; min-width: 0; }
.cmp-video { display: block; width: 100%; max-width: 100%; height: auto; max-height: 58vh; object-fit: contain; background: #000; border-radius: 8px; }
.cmp-label { font-size: 13px; font-weight: 600; margin-bottom: 6px; }
.cmp-none { border: 1px dashed #909399; border-radius: 8px; padding: 40px 0; text-align: center; color: #909399; font-size: 13px; }
.cmp-tip { font-size: 12px; color: #909399; }
@media (max-width: 767px) {
  .card-header { flex-direction: column; align-items: flex-start; gap: 8px; }
  .el-table { overflow-x: auto; }
}
</style>
